import { Box, Loader } from '@mantine/core';
import UnderlineTabsDeprecated from '../UI/page-nav/UnderlineTabsDeprecated';
import { ClientsTable } from './ClientsTable';
import { FamiliesTable } from './FamiliesTable';
import { ClientTabs, useClientTab } from './hooks/useClientTab';
import { useParams, useSearchParams } from 'react-router';
import { FamilyClientsTable } from './components/FamilyClientsTable';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClientsContext } from 'src/clients/context/ClientsContext';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSessionStorage } from '@mantine/hooks';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { useClients } from './hooks/useClients';
import { ClientsEmptyState } from './components/ClientsEmptyState';
import { useFlags } from 'src/feature-flags/FlagsHook';

export default function ClientsPage() {
  const { currentTab, setTab } = useClientTab();
  const { id } = useParams<{ id?: string }>();
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();
  const { startClientCreation } = useClientsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const handledCreate = useRef(false);
  const { setSidebarFullScreen, setSidebarContent, setPreventSidebarCollapse } =
    useAppContainerContext();
  const { flagEnabledSiestaSteering } = useFlags();

  const { clients, isLoading: isClientsLoading } = useClients();
  const [emptyStateDismissed, setEmptyStateDismissed] = useSessionStorage({
    key: 'clients-empty-state-dismissed',
    defaultValue: false,
  });

  const showEmptyState =
    flagEnabledSiestaSteering && !isClientsLoading && clients.length === 0 && !emptyStateDismissed;

  const dismissEmptyState = () => {
    setEmptyStateDismissed(true);
    setSidebarFullScreen(false);
    setSidebarContent(null);
  };

  const handleAddManually = () => {
    dismissEmptyState();
    startClientCreation();
  };

  useLayoutEffect(() => {
    if (!showEmptyState) return;

    setSidebarFullScreen(true);
    setPreventSidebarCollapse(true);
    setSidebarContent(
      <ClientsEmptyState onAddManually={handleAddManually} onDismiss={dismissEmptyState} />,
    );

    return () => {
      setSidebarFullScreen(false);
      setPreventSidebarCollapse(false);
      setSidebarContent(null);
    };
  }, [showEmptyState]);

  useEffect(() => {
    if (searchParams.get('create') === 'true' && !handledCreate.current) {
      handledCreate.current = true;
      const firstName = searchParams.get('first_name') ?? undefined;
      const lastName = searchParams.get('last_name') ?? undefined;
      const emailAddress = searchParams.get('email_address') ?? undefined;
      const prefill =
        firstName || lastName || emailAddress ? { firstName, lastName, emailAddress } : undefined;
      startClientCreation(prefill);
      setSearchParams(
        prev => {
          prev.delete('create');
          prev.delete('first_name');
          prev.delete('last_name');
          prev.delete('email_address');
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, startClientCreation, setSearchParams]);

  const tabContent = getTabContent(currentUser, currentTab, id, startClientCreation);

  if (isCurrentUserLoading) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box p="md">
      <UnderlineTabsDeprecated
        value={currentTab}
        onChange={value => {
          if (value != null) {
            setTab(value as ClientTabs);
          }
        }}
      >
        <UnderlineTabsDeprecated.List>
          <UnderlineTabsDeprecated.Tab value={ClientTabs.Clients}>
            Clients
          </UnderlineTabsDeprecated.Tab>
          <UnderlineTabsDeprecated.Tab value={ClientTabs.Families}>
            Families
          </UnderlineTabsDeprecated.Tab>
        </UnderlineTabsDeprecated.List>

        <Box mt="sm">{tabContent}</Box>
      </UnderlineTabsDeprecated>
    </Box>
  );
}

function getTabContent(
  currentUser: { id?: string; userRole: string } | undefined,
  currentTab: string | undefined,
  id: string | undefined,
  startClientCreation: () => void,
) {
  if (!currentUser?.id) return null;

  const filters = ['SYSTEM_ADMIN', 'SERVICE_ADMIN'].includes(currentUser.userRole)
    ? undefined
    : { advisorUserId: currentUser.id };

  if (currentTab === ClientTabs.Clients) {
    return (
      <ClientsTable
        filters={filters}
        primaryButton={{
          label: 'Add client',
          onClick: startClientCreation,
        }}
      />
    );
  }
  if (currentTab === ClientTabs.Families) {
    if (id != null) {
      return <FamilyClientsTable id={id} />;
    }

    return <FamiliesTable filters={filters} />;
  }
  return null;
}
